// Import Packages
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Editor, EditorState, CompositeDecorator, ContentState, Modifier, SelectionState } from 'draft-js';
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { CheckIfFlowUpdated } from '../../../helpers/helper';
import 'prismjs/components/prism-python';
import { ResizableBox } from 'react-resizable';

// Import Material UI
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import { Chip, IconButton, TextField } from '@material-ui/core';
import FilterCenterFocusIcon from '@material-ui/icons/FilterCenterFocus';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import { Autocomplete } from '@material-ui/lab';


// Import Pages
import { VariableModal } from '../VariableModal/VariableModal';
import {
    lastFocusField,
    inputRefresh,
    showObjectExplorerIcon,
    activeAutom,
    selectedField,
    initialActions, saveAvailable, changedFields,
    alertState
} from "../../../recoil-store/recoil-state";
import ObjectExplorer from './components/ObjectExplorer/ObjectExplorer_new_design';
import RichTextEditor from "react-rte";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { EditorHelper } from '../../../helpers/editorHelper';
import CommonModal from '../CommonModal/CommonModal';
import MateDrive from '../../MateDrive/MateDrive';
import MathFunc from '../../MathFunc/MathFunc';
import DeleteConfirm from '../../DataTable/DeleteConfirm';
import { getAllMathFunctions, mathFunctionGetById } from '../../MathFunc/Functions/Functions';

// Import Icons
import MateDriveIcon from '../../../assets/icons/moduleIcons/mate-drive-link-icon.svg'
import FxIcon from '../../../assets/icons/moduleIcons/math-func.svg'
import Close from '@material-ui/icons/Close';
import maximizeIcon from '../../../assets/icons/dataManager/maximize-icon.svg';

// Import Constants
import { UNIQUE_SEPERATOR } from '../../../helpers/constant'

// Import CSS
import './TextInput.css'
import 'react-resizable/css/styles.css';

/**
 * @description This function lets you return component according to the name of the incoming action.
 * @param       action - object
 * @param       inputName - string
 * @param       variables - object variable list
 * @param       multiline - boolean
 * @param       onBlur - function
 * @param       include - array
 * @param       disabled - boolean
 * @param       setOpenVariable - open variable function that allows us to open
 * @param       isRequired - boolean
 * @param       isCredentialPickerValue - boolean
 * @param       inputMode - string
 * @returns     Component
 * @author      ibrahim AKIN
 */

const styles = {
    handle: {
        color: '#000',
        fontWeight: 'bold',
        direction: 'ltr',
        unicodeBidi: 'bidi-override'
    }
};

const HANDLE_REGEX = /(##[\w-]+(\[[-\w\s]+\])*##)/g;
const FUNCTIONS = getAllMathFunctions();

// Her bir fonksiyon için bir regex oluştur
const functionRegexes = FUNCTIONS.map(func => {
    return func.functionName;
});

// HANDLE_MATH_REGEX'i oluştur
// const HANDLE_MATH_REGEX = new RegExp(`fx\\.(${functionRegexes.join('|')})\\(.*?\\|[\\w\\d]{6}\\)`, 'g')
const HANDLE_MATH_REGEX = new RegExp(`fx\\.(${functionRegexes.join('|')})\\((.*?&.\\|.&[\\w\\d]{6}|.*?\\|[\\w\\d]{6})\\)`, 'g')

// fx.Difference((asdasd)|1|8Hzi5U)
//  önceki çalışan => /(##\w*(\[+\w+\]+)*##)/g;
//  bu parantez içinde boşluk hertürlü kabul ediyor => /(##[\w-]+(\[[^\[\]]+\])*(##))/g

function handleStrategy(contentBlock, callback) {
    findWithRegex(HANDLE_REGEX, contentBlock, callback);
}


function findWithRegex(regex, contentBlock, callback) {
    const text = contentBlock.getText();
    let matchArr, start;
    while ((matchArr = regex.exec(text)) !== null) {
        start = matchArr.index;
        callback(start, start + matchArr[0].length);
    }
}

const HandleSpan = (props) => {
    return (
        <span style={styles.handle} data-offset-key={props.offsetKey}>
            {props.children}
        </span>
    );
};

const parseString = (input) => {
    const matches = input.match(/fx\.[^(]+\((.*)\)/);

    if (matches) {
        let seperator;
        if (matches[1].includes(UNIQUE_SEPERATOR)) {
            seperator = UNIQUE_SEPERATOR
        } else {
            seperator = "|"
        }
        const innerContent = matches[1].split(seperator).map(str => str.trim());
        const functionName = input.split('.')[1].split('(')[0];
        return { innerContent, functionName };
    }

    return null;
};

function TextInput({
    action,
    inputName,
    variables,
    multiline,
    onBlur,
    include,
    recorder,
    disabled,
    isRequired,
    setOpenVariable,
    inputMode = 'text',
    isVariableAdd = true,
    refresh = false,
    defaultValue = undefined,
    isCredentialPickerValue,
    setField,
    validation,
    fieldType,
    displayName,
    type,
    hideRequired,
    action_id,
    autom_id,
    fieldName,
    subInputName,
    ignoreSave,
    outLabel,
    showOpenModal,
    onfocus,
    outNewValue,
    outOnChange,
    isPassword,
    mateDrivePath,
    justValue,
    submit,
    className,
    placeHolder,
    mockInputName,
    limit
}) {
    const [commonModal, setCommonModal] = useState(false);
    const [inputRefreshGlobal] = useRecoilState(inputRefresh);
    const [showOEIcon, setShowOEIcon] = useRecoilState(showObjectExplorerIcon);
    const [openDrive, setOpenDrive] = useState(false);
    // const [selectedMathFunction, setSelectedMathFunction] = useState(false);
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
    const [selectedMathFunc, setSelectedMathFunc] = useState("");
    const [automDetail, setAutomDetail] = useRecoilState(activeAutom);
    const [richEditorState, setRichEditorState] = useState({ editorHtml: '', theme: 'snow' })
    const [editorWidth, setEditorWidth] = useState("100%");
    const [editorHeight, setEditorHeight] = useState(250);
    const [selectedFieldValue, setSelectedFieldValue] = useRecoilState(selectedField);
    const [focusField, setFocusField] = useRecoilState(lastFocusField);
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const setChangeFields = useSetRecoilState(changedFields);
    const changedFieldsState = useRecoilValue(changedFields);
    const [selectFunction, setSelectFunction] = useState("");
    const [openFuncOptions, setOpenFuncOptions] = useState(false);
    const [mathFunctions, setMathFunctions] = useState([]);
    const [selectedMathFunction, setSelectedMathFunction] = useState(undefined);
    const setAlert = useSetRecoilState(alertState);

    const quillRef = useRef(null)

    const HandleMathChip = (props) => {
        // fonksiyon ismi ile değerlerini değişkenlere atıyoruz.
        const { innerContent, functionName } = parseString(props.decoratedText)
        // Sadece fonksiyon ismini göstereceğimiz için arka plandaki yazının tam hali ile fonksiyon ismi arasındaki farkı bulup o kadar " " ekliyoruz.
        let l = ' '.repeat(props.decoratedText.length - functionName.length);

        return (
            <>
                <span {...props} data-offset-key={props.offsetKey} onDoubleClick={(e) => {
                    const findedFunc = findAndFillFunction(innerContent, functionName)
                    setSelectedMathFunc(props.decoratedText)
                    setSelectedMathFunction(findedFunc)
                }} className='function-chip-container'>
                    <div className='chip-body' contentEditable={false} >
                        <img style={{ width: "16px", height: "16px", verticalAlign: "text-bottom" }} src={FxIcon} />
                        <span data-offset-key={props.offsetKey} className='chip-function-name'>
                            {functionName}
                            {/* Burda uzunluğu farklı bir değer gösterdiğimiz için uzunluğu eşitlemek adına alttaki spani oluşturup css ile görünmez hale getirdik.
                            Bu sayede focus olunan alan bozulmamış oldu. */}
                            <span className='zero-font'>{l}</span>
                        </span>
                        <span className='column-line'></span>
                        <img className='chip-open-icon size-icon'
                            src={maximizeIcon} onClick={() => {
                                const findedFunc = findAndFillFunction(innerContent, functionName)
                                setSelectedMathFunc(props.decoratedText)
                                setSelectedMathFunction(findedFunc)
                            }} />
                        <Close contentEditable={false} onClick={() => {
                            setSelectedMathFunc(props.decoratedText)
                            setOpenDeleteConfirm(true)
                        }} style={{ width: "13px", height: "16px", verticalAlign: "middle", cursor: "pointer" }} />
                    </div>
                </span>
            </>
        );
    };

    const findAndFillFunction = (innerContent, functionName) => {
        const data = getAllMathFunctions()
        let findedFunc = data.find((func) => func.functionName === functionName)
        const findedFuncCopy = JSON.parse(JSON.stringify(findedFunc))
        findedFuncCopy?.fields.forEach((field, index) => { //fonkstyionların kontrol için alanlarda dön
            innerContent.forEach((text, i) => { // yazılan alanlarda dön.
                let isOk
                if (innerContent.length - 1 === i) {
                    return
                }
                if (field.format && text) {
                    isOk = true
                    const formatIndex = text.indexOf(':')
                    if (formatIndex !== -1 || (field.checkbox && field.checkbox[1].replaced === text)) {
                        const formatText = text.substring(0, formatIndex);
                        const formatAfterText = text.slice(formatIndex + 1).trim();
                        if (field.format.includes(formatText)) {
                            field.value = formatAfterText
                            return;
                        }
                    }
                }
                if (field.options && text) {
                    if ((field.options && field.options[0].value === text) || (field.options && field.options[1].value === text)) {
                        field.value = text
                        return;
                    }
                }
                else if (field.isRequired && (text && i === index)) {
                    field.value = text
                    return
                }
                else if (!field.isRequired && (text && i === index) && !isOk) {
                    field.value = text
                    return
                }
            })
        })

        if (findedFuncCopy) {
            // fonksiyon alanına id ekliyoruz.
            findedFuncCopy.identifier = innerContent[innerContent.length - 1]
            return findedFuncCopy
        }
        return undefined
    }

    function handleMathStrategy(contentBlock, callback) {
        findWithRegex(HANDLE_MATH_REGEX, contentBlock, callback);
    }

    const compositeDecorator = useRef(new CompositeDecorator([
        {
            strategy: handleMathStrategy,
            component: HandleMathChip
        }, {
            strategy: handleStrategy,
            component: HandleSpan
        },

    ]
    ));

    const [open, setOpen] = useState(false);
    const [showObjectExporer, setShowObjectExplorer] = useState(false);

    const [dropdownValue, setDropdownValue] = useState({});
    const [indexValue, setIndexValue] = useState(0);
    const { t } = useTranslation();
    const editor = useRef(false);
    const validate = useRef(false);

    useEffect(() => {
        if (action?.fields) {
            if (!action.fields[inputName]) {
                action.fields[inputName] = { isRequired, value: (defaultValue || defaultValue === 0) ? defaultValue : '' };
                if (displayName) {
                    action.fields[inputName].conditions = [];
                }
                if (setField) {
                    // setField(inputName, action.fields[inputName]);
                }
            }
        } else if (inputName) {
            if (action && !action[inputName] && !justValue) {
                action[inputName] = { isRequired, value: defaultValue ? defaultValue : '' };
            }
        }
        setState(createState(getValue()));

        if (type === "html") {
            setRichEditorState({ editorHtml: action.fields[inputName].value, theme: 'snow' })
        }
    }, [action, inputName, disabled, refresh, inputRefreshGlobal]);

    useEffect(() => {
        const data = getAllMathFunctions()
        if (data.length) {
            setMathFunctions(data)
        }
    }, []);

    useEffect(() => {
        let actionId;
        if (action?.id) {
            actionId = action.id;
        } else if (action_id) {
            actionId = action_id;
        }
        if (outNewValue?.isOk && (outNewValue.value || outNewValue.value === "") && action?.fields && action.fields[inputName]) {
            action.fields[inputName].value = outNewValue.value
            const field = initialActionsData[actionId]?.fields[inputName];
            const oldValue = !field ? undefined : field.value;
            CheckIfFlowUpdated(inputName, action, oldValue, outNewValue.value, setSaveAvailable, setChangeFields, changedFieldsState);

            setState(createState(outNewValue.value));
        }
    }, [outNewValue?.value, outNewValue?.isOk])


    const getValue = () => {
        if (action) {
            if (action?.fields && (inputName || inputName === 0)) {
                return action.fields[inputName]?.value?.length ? action.fields[inputName].value : '';
            } else if (inputName || inputName >= 0) {
                if (justValue) {
                    return action[inputName] ? action[inputName] : '';
                }
                return action[inputName]?.value ? action[inputName].value : '';
            } else {
                return action.value ? action.value : '';
            }
        } else {
            return '';
        }
    };

    const checkValidate = async (validateConfiguration) => {
        let isValidate = true;

        if (validateConfiguration.length) {
            validateConfiguration.forEach(item => {
                switch (item.type) {
                    case "length":
                        const newValue = state.editorState.getCurrentContent().getPlainText().length;
                        if (item.validation.operator === "lt") {
                            isValidate = newValue < item.validation.value;
                            break;
                        } else if (item.validation.operator === "gt") {
                            isValidate = newValue > item.validation.value;
                            break;
                        } else {
                            isValidate = newValue === item.validation.value;
                            break;
                        }
                    default:
                        isValidate = true;
                        break;
                }
            })
        }

        return isValidate;
    }

    // c => conditions
    const setValue = (v, c) => {
        if (action?.fields && (inputName || inputName === 0)) {
            // if (action.fields[inputName]) {
            //     action.fields[inputName].value = v;
            //     action.fields[inputName].isRequired = isRequired;

            // } else {
            //     action.fields[inputName] = { value: v, isRequired };
            // }
            action.fields[inputName] = { ...action.fields[inputName], value: v, isRequired };
            // if (c) {
            //     action.fields[inputName].conditions = c;
            //     const anyActionHasCondition = c.some(function (a) { return a.length });
            //     if (!anyActionHasCondition) {
            //         if (action.conditions) {
            //             const fieldIndex = action.conditions.indexOf(inputName);
            //             action.conditions.splice(fieldIndex, 1);
            //         }
            //     } else if (action.conditions) {
            //         if (!action.conditions.find(x => x === inputName)) {
            //             action.conditions.push(inputName);
            //         }
            //     } else {
            //         action.conditions = [inputName];
            //     }
            // }
            if (setField) {
                setField(inputName, action.fields[inputName]);
            }
        } else if (inputName || inputName >= 0) {
            if (justValue) {
                action[inputName] = v;
            } else {
                action[inputName] = {
                    ...action[inputName],
                    value: v,
                    isRequired
                };
            }
            if (setField) {
                setField(inputName, action[inputName]);
            }
        } else if (action) {
            action.value = v;
            if (setField) {
                setField(v);
            }
        }
    };

    const createState = (value) => {
        return ({
            editorState: EditorState.createWithContent(
                ContentState.createFromText(String(value)),
                compositeDecorator.current
            )
        });
    };

    const [state, setState] = useState(createState(getValue()));

    useEffect(() => {
        const newValue = state.editorState.getCurrentContent().getPlainText();
        if (newValue.includes("fn.")) {
            // setOpenMathSelect(true)
            const element = document.getElementById(`function-select-${inputName}`);
            const elementDiv = document.querySelector(`.function-select-${inputName}`);
            const elementInput = document.querySelector(`.notranslate.public-DraftEditor-content`);
            elementDiv.style.display = "block";
            element.focus()
            showOptions()
        }
    }, [state.editorState.getCurrentContent().getPlainText()])

    const handleBeforeInput = (input) => {
        if (limit) {
            const currentContent = state.editorState.getCurrentContent();
            const currentLength = currentContent.getPlainText('').length;
            if (currentLength + input.length > limit) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t('CONTENT.GENERAL.CHARACTER-LIMIT', { limit: limit }),
                    severity: 'error'
                }))
                return 'handled'; // Prevent the input
            }
            return 'not-handled';
        }
    };

    const handlePastedText = (text) => {
        if (limit) {
            const currentContent = state.editorState.getCurrentContent();
            const currentLength = currentContent.getPlainText('').length;
            if (currentLength + text.length > limit) {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t('CONTENT.GENERAL.CHARACTER-LIMIT', { limit: limit }),
                    severity: 'error'
                }))
                return 'handled'; // Prevent the paste
            }
            return 'not-handled';
        }
    };

    const onChange = async (editorState, fromMateDrive) => {
        if (!fromMateDrive) {
            // check function deleted
            const currentContent = editorState.getCurrentContent().getPlainText();
            const previousContent = state.editorState.getCurrentContent().getPlainText();
            if (previousContent.length > currentContent.length) {

                const variableArr = previousContent.match(HANDLE_MATH_REGEX);
                if (variableArr && variableArr.length) {
                    //HANDLE_MATH_REGEX.lastIndex = 0;
                    const curVariableArr = currentContent.match(HANDLE_MATH_REGEX);
                    if (!curVariableArr || variableArr.length !== curVariableArr.length) {
                        for (let v of variableArr) {
                            if (!currentContent.includes(v)) {
                                setSelectedMathFunc(v)
                                setOpenDeleteConfirm(true)
                            }
                        }

                        setState({ editorState: state.editorState });
                        return;
                    }
                }

            }
        }
        // const cv = await checkValidate(validation);
        // console.log("CV", cv)
        // if (cv) {
        let actionId;
        if (action.id) {
            actionId = action.id;
        } else if (action_id) {
            actionId = action_id;
        }
        if (!ignoreSave && actionId) {
            // if (initialActionsData[actionId]) {
            // const newValue = editorState.getCurrentContent().getPlainText();
            // if (fieldName) {
            //     // fieldName => It is the key name under the fields object. It is not mandatory.
            //     let field = initialActionsData[actionId]?.fields[fieldName]
            //     if (field && (field[subInputName] || field.value[subInputName])) {
            //         field = field.value ? field.value[subInputName] : field[subInputName];
            //     }
            //     if (field && field[inputName]) {
            //         field = field[inputName];
            //     }
            //     const oldValue = !field ? undefined : field.value;
            //     CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState, fieldName, subInputName);
            // } else {
            //     const field = initialActionsData[actionId]?.fields[inputName];
            //     const oldValue = !field ? undefined : field.value;
            //     CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
            // }
            const newValue = !fromMateDrive ? editorState.getCurrentContent().getPlainText() : editorState;
            const field = initialActionsData[actionId]?.fields[inputName];
            const oldValue = !field ? undefined : field.value;
            CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
            if (outOnChange) {
                outOnChange(newValue)
            }
        }
        //  else {
        //     // temporarily left that way. An improvement will be made for cases where there is no id in the action
        //     setSaveAvailable(true);
        // }
        if (fromMateDrive) {
            setValue(editorState);
            setState(createState(editorState));
            setOpenDrive(false)
            return
        }
        setValue(editorState.getCurrentContent().getPlainText());
        setState({ editorState });
    };

    const deleteMathFunc = () => {
        const currentContent = state.editorState.getCurrentContent().getPlainText();
        const modifiedString = currentContent.replace(selectedMathFunc, '');
        setState(createState(modifiedString));
    }

    // const selectMateDrive = (editorState, fromMateDrive) => {
    //     let newEditor = insertText(editorState, state.editorState);

    //     // onChange(editorState, fromMateDrive)
    //     if (submit) {
    //         submit(newEditor.getCurrentContent().getPlainText())
    //     }

    //     let actionId;
    //     if (action.id) {
    //         actionId = action.id;
    //     } else if (action_id) {
    //         actionId = action_id;
    //     }

    //     let newValue = newEditor.getCurrentContent().getPlainText();
    //     if (!ignoreSave && actionId) {
    //         const field = initialActionsData[actionId]?.fields[inputName];
    //         const oldValue = !field ? undefined : field.value;
    //         CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
    //         if (outOnChange) {
    //             outOnChange(newValue)
    //         }
    //     }

    //     setValue(newValue);
    //     setState({ editorState: newEditor });
    //     setOpenDrive(false)
    // }

    const selectMateDrive = (textToInsert) => {
        const contentState = state.editorState.getCurrentContent();

        // Create a selection state that covers all text
        const selection = SelectionState.createEmpty(contentState.getFirstBlock().getKey()).merge({
            anchorOffset: 0,
            focusOffset: contentState.getPlainText().length,
        });

        // Replace all existing text with the new value
        const newContentState = Modifier.replaceText(contentState, selection, textToInsert);

        // Create a new editor state with the replaced text and set cursor at end
        const newEditor = EditorState.push(state.editorState, newContentState, 'insert-characters');

        // Update the state and save the new value as needed
        const newValue = newEditor.getCurrentContent().getPlainText();
        setValue(newValue);
        setState({ editorState: newEditor });
        setOpenDrive(false);

        // Optional: Trigger any additional save or onchange functionality
        if (submit) {
            submit(newValue);
        }

        if (action && action.id) {
            const field = initialActionsData[action.id]?.fields[inputName];
            const oldValue = field ? field.value : undefined;
            CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
            if (outOnChange) {
                outOnChange(newValue);
            }
        }
    };

    const onChangeRichText = async (newValue) => {
        const newEditorHtml = { editorHtml: newValue, theme: richEditorState.theme }
        setRichEditorState(newEditorHtml);
        // const cv = await checkValidate(validation);
        // console.log("CV", cv)
        // if (cv) {

        const field = initialActionsData[action.id]?.fields[inputName];
        let oldValue = !field ? undefined : field.value;

        if (newValue.toString("html").replace(/<\/?[^>]+(>|$)/g, "").trim() === "") {
            if (oldValue === "") oldValue = "<p><br></p>";
        }
        CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
        setValue(newValue);
        // }
    };

    const handleAddClick = async () => {
        // let newValue;
        // const field = initialActionsData[dropdownValue.id].fields[inputName];
        // const oldValue = !field ? undefined : field.value;
        let newEditor
        if (type === "html") {
            const text = dropdownValue.type === 'array' ? '##' + dropdownValue.name + '[' + indexValue + ']##' : '##' + dropdownValue.name + '##'
            handleAddQuillText(text)
        }
        if (dropdownValue.type === 'array' && indexValue !== null) {
            //  newValue = '##' + dropdownValue.name + '[' + indexValue + ']##';
            newEditor = insertText('##' + dropdownValue.name + '[' + indexValue + ']##', state.editorState);
            setState({ editorState: newEditor });
        } else {
            //  newValue = '##' + dropdownValue.name + '##';
            newEditor = insertText('##' + dropdownValue.name + '##', state.editorState)
            setState({ editorState: newEditor });
        }
        if (submit) {
            submit(newEditor.getCurrentContent().getPlainText())
        }
        // CheckIfFlowUpdated(inputName, dropdownValue, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
        setOpen(false);
    };

    const insertText = (text, editorState) => {
        const currentContent = editorState.getCurrentContent(), currentSelection = editorState.getSelection();
        const newContent = Modifier.replaceText(currentContent, currentSelection, text);
        const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');
        return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter());
    };

    const handleAddQuillText = (text) => {
        const editor = quillRef.current.getEditor();
        if (editor) {
            const selection = editor.getSelection();
            const cursorPosition = selection ? selection.index : editor.getLength(); // İmleç pozisyonu veya sonuna ekle
            editor.insertText(cursorPosition, text);
            editor.setSelection(cursorPosition + text.length);
        }
    };

    useEffect(() => {
        if (selectedFieldValue.path.length && focusField.inputName && (focusField.inputName === inputName || focusField.inputName === mockInputName)) {

            const valueEndIndex = selectedFieldValue.path.indexOf('[')
            let firstValue;
            let secondValue;
            if (valueEndIndex !== -1) {
                firstValue = selectedFieldValue.path.substring(2, valueEndIndex)
                secondValue = selectedFieldValue.path.substring(valueEndIndex)
            } else {
                firstValue = selectedFieldValue.path.substring(2, selectedFieldValue.path.length - 2)
                secondValue = selectedFieldValue.path.substring(selectedFieldValue.path.length - 2);
            }
            let v;
            if (selectedFieldValue.key === '') {
                v = `##${firstValue}${secondValue}`;
            } else {
                v = `##${selectedFieldValue.key}[${firstValue}]${secondValue}`;
            }
            let actionId;
            if (action.id) {
                actionId = action.id;
            } else if (action_id) {
                actionId = action_id;
            }
            if (!ignoreSave && actionId) {
                const newValue = state.editorState.getCurrentContent().getPlainText() + v
                const field = initialActionsData[actionId]?.fields[inputName];
                const oldValue = !field ? undefined : field.value;
                CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
                setValue(newValue)
            }
            setState({ editorState: insertText(v, state.editorState) });
            setSelectedFieldValue({
                key: '',
                name: '',
                type: '',
                path: []
            });
            // if (!focusField.is_sys_field) {

            //     // setValue(editorState.getCurrentContent().getPlainText());
            //     // if (props.selectedAction.fields[focusField.inputName].value) {
            //     //     props.selectedAction.fields[focusField.inputName].value += v;
            //     // } else {
            //     //     props.selectedAction.fields[focusField.inputName].value = v;
            //     // }

            // } else {
            //     // props.selectedAction.fields[sysActionFielName].value[focusField.inputName].value = v;
            // }
            // setInputRefreshGlobal(new Date());
        }
    }, [selectedFieldValue])


    const handleClickOpen = () => {
        setOpen(true);
    };

    const requireValidate = (!getValue() && isRequired) && !isCredentialPickerValue;
    const errorValidate = inputMode === 'text' ? requireValidate : requireValidate || !validate.current;

    const handleOnFocus = () => {
        if (inputName) {
            setFocusField({
                inputName,
                type: include && include.length ? include[0] : '',
                //is_sys_field: !action.fields
            });
        } else if (mockInputName) {
            setFocusField({
                inputName: mockInputName,
                type: include && include.length ? include[0] : '',
                //is_sys_field: !action.fields
            });
        }

    }
    const addChip = async (newText, funcText) => {
        setValue(newText);
        setState(createState(newText));
    }

    const handleChangeFunction = async (value, isShowDefault) => {
        setSelectFunction(value)
    }
    const showOptions = async () => {
        setSelectedMathFunc("")
        setOpenFuncOptions(true)
    }
    const handleClose = async () => {
        const elementDiv = document.querySelector(`.function-select-${inputName}`);
        elementDiv.style.display = "none";
        setOpenFuncOptions(false)
        setSelectFunction("")
    }
    const handleOptionClick = async (id) => {
        if (id) {
            await handleClose()
            const selectedFunction = mathFunctionGetById(id)
            setSelectedMathFunction(JSON.parse(JSON.stringify(selectedFunction)))
            const elementDiv = document.querySelector(`.function-select-${inputName}`);
            elementDiv.style.display = "none";
        }
    }

    const handleShowObjectExplorer = () => {
        if (showOEIcon) {
            setShowOEIcon(false);
        }
        setShowObjectExplorer(true);
    }

    const cancelObjectExplorer = () => {
        setShowOEIcon(true);
        setShowObjectExplorer(false);
    }

    const saveObjectExplorer = (f, conditionExist) => {
        if (action.fields && inputName) {
            action.fields[inputName].functions = f;
        }

        //     action.fields[inputName].conditions = c;
        //    // const anyActionHasCondition = c.some(function (a) { return a.length });
        if (!conditionExist) {
            if (action.conditions) {
                const fieldIndex = action.conditions.indexOf(inputName);
                action.conditions.splice(fieldIndex, 1);
            }
        } else if (action.conditions) {
            if (!action.conditions.find(x => x === inputName)) {
                action.conditions.push(inputName);
            }
        } else {
            action.conditions = [inputName];
        }


        //setValue(data.value, data.conditions)
        // EditorState.set(editorState, data.value);
        //setState(createState(data.value)); // insertText(data.value, state.editorState)
        setShowOEIcon(true);
        setShowObjectExplorer(false);
        let copyItems = JSON.parse(JSON.stringify(automDetail.items))
        findAndUpdateItem(copyItems)
        setAutomDetail({
            ...automDetail,
            items: copyItems
        })
    }

    const findAndUpdateItem = (items) => {
        items.forEach(item => {
            if (item.item_id === action.item_id) {
                item.fields = JSON.parse(JSON.stringify(action.fields))
                item.conditions = JSON.parse(JSON.stringify(action.conditions))
            } else if (item.children) {
                findAndUpdateItem(item.children)
            }
        })
    }

    const saveNewValue = (v, selectedVariableName) => {

        if (action.fields && inputName) {
            action.fields[inputName] = { ...action.fields[inputName], value: v, selectedVariableName, isRequired };
            if (setField) {
                setField(inputName, action.fields[inputName]);
            }
        }
        setState(createState(v));
        setShowOEIcon(true);
        setShowObjectExplorer(false);
    }

    const openCommonModal = () => {
        setCommonModal(true);
    }

    const closeCommonModal = () => {
        let newValue = richEditorState.editorHtml;//state.editorState.getCurrentContent().getPlainText()
        const field = initialActionsData[action.id]?.fields[inputName];
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
        setValue(newValue);
        setCommonModal(false);
    }

    const saveCommonModal = () => {
        const newValue = getValue();
        const field = initialActionsData[action.id]?.fields[inputName];
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated(inputName, action, oldValue, newValue, setSaveAvailable, setChangeFields, changedFieldsState);
        onChangeRichText(newValue);
        setCommonModal(false);
    }

    const handleShowDrive = () => {
        setOpenDrive(true);
    }

    const ContentToModal = () => {
        return (
            <TextInput showOpenModal={false} type="html" action={action} inputName="body" variables={variables} setOpenVariable={setOpenVariable}
                multiline fieldType="string" displayName={t(outLabel + 'BODY')} />
        )
    }

    const handleResize = (event, { size }) => {
        if (!showOpenModal && size.height > 255) {
            setEditorWidth(size.width);
            setEditorHeight(size.height);
        }
    };

    return (
        <>
            {commonModal && (
                <CommonModal save={saveCommonModal} closeModal={closeCommonModal} content={<ContentToModal />} />
            )}
            {requireValidate && inputMode === 'text' && !hideRequired &&
                <small style={{ color: '#D75E5E' }}>&nbsp; {t('FLOW-MESSAGES.LIBRARY.GENERAL.REQUIRED')}</small>}
            <div style={{ position: "relative" }}>
                <div className='new-text-input-container'>
                    <div className={`editable${multiline ? ' multiline' : ''} ${(showOEIcon && fieldType && (fieldType.includes('string') || state.editorState.getCurrentContent().getPlainText()))
                        && ["filePath", "folderPath"].includes(inputName) && autom_id && "small-editable"} ${className ? className : ""}`}
                        style={{
                            minHeight: multiline && 100,
                            border: errorValidate && '1px solid #E6E6E6',
                            backgroundColor: "#FCFCFC"
                        }}
                        onClick={() => {
                            // setIsFocused(true)
                            onfocus && onfocus()
                        }}
                    >
                        {type === "html" ? (
                            <>
                                <ResizableBox
                                    width={editorWidth}
                                    height={editorHeight}
                                    minConstraints={[100, 100]}
                                    maxConstraints={[800, 800]}
                                    onResize={handleResize}
                                >
                                    <ReactQuill theme="snow"
                                        value={richEditorState.editorHtml}
                                        onChange={value => onChangeRichText(value)}
                                        modules={EditorHelper.modules}
                                        ref={quillRef}
                                        style={!showOpenModal ? { width: '100%', height: 'calc(100% - 75px)' } : { width: '100%', height: 'calc(100% - 157px)' }}
                                        formats={EditorHelper.formats} />
                                </ResizableBox>
                            </>

                        ) : <div style={{ position: "relative" }} >
                            <Editor onFocus={() => {
                                handleOnFocus()
                                onfocus && onfocus()
                            }} editorState={state.editorState}
                                onChange={(e) => {
                                    onChange(e)
                                }}
                                onBlur={(e) => {
                                    onBlur && onBlur(e)
                                }}
                                handleBeforeInput={handleBeforeInput}
                                handlePastedText={handlePastedText}
                                ref={editor}
                                placeholder={placeHolder}
                                readOnly={recorder || disabled} style={{ minHeight: multiline && 100 }} >

                            </Editor>

                        </div>
                        }
                        {
                            (mateDrivePath || ["filePath", "folderPath"].includes(inputName)) && autom_id && (
                                <IconButton className="mate-drive-button" style={{ position: 'absolute' }} onClick={handleShowDrive} disabled={disabled} >
                                    <img src={MateDriveIcon} />
                                </IconButton>
                            )
                        }

                    </div>
                    {(showOEIcon && fieldType && (fieldType.includes('string') || state.editorState.getCurrentContent().getPlainText())) && ["filePath", "folderPath"].includes(inputName) && autom_id &&
                        <IconButton className="object-explorer-button" style={{ position: 'absolute', float: 'rigth' }}
                            onClick={handleShowObjectExplorer}>
                            <FilterCenterFocusIcon style={{ paddingTop: 5, color: "#8C90A2" }} />
                        </IconButton>
                    }
                    {
                        showOpenModal && (
                            <IconButton className="expand-button" style={{ position: 'absolute' }} onClick={openCommonModal}>
                                <AspectRatioIcon style={{ paddingTop: 5, color: "#8C90A2" }} />
                            </IconButton>
                        )
                    }
                    {isVariableAdd &&
                        <IconButton className="add-variable-button" onClick={handleClickOpen}
                            disabled={disabled}>
                            <AddCircleOutlineIcon className="add-variable-icon" />
                        </IconButton>}
                    {open && (
                        <VariableModal setOpen={setOpen} variables={variables}
                            dropdownValue={dropdownValue} setDropdownValue={setDropdownValue}
                            setOpenVariable={setOpenVariable}
                            indexValue={indexValue} setIndexValue={setIndexValue}
                            handleAddClick={handleAddClick} include={include}
                        />
                    )}
                    <Autocomplete
                        onChange={(event, value, reason) => {
                            handleOptionClick(value ? value.id : "")
                            handleChangeFunction(value ? value : "")
                        }}
                        value={selectFunction}
                        id={`function-select-${inputName}`}
                        className={`function-select-input function-select-${inputName}`}
                        size="small"
                        open={openFuncOptions}
                        onClose={() => { handleClose() }}
                        onFocus={() => { showOptions() }}
                        options={mathFunctions.length > 0 ? mathFunctions : []}
                        getOptionLabel={(item) => {
                            if (item.functionName) {
                                return item.functionName
                            }
                            return item
                        }}
                        renderInput={(params) => <TextField {...params} variant="outlined" />}
                    // renderGroup={(option) => (
                    //     <div style={{ width: "100%", height: "100%" }} onClick={handleOptionClick}>
                    //         {option.name}
                    //     </div>
                    // )}
                    // renderOption={(option) => (
                    //     <div style={{ width: "100%", height: "100%" }} >
                    //         {option.functionName}
                    //     </div>
                    // )}
                    />
                </div >
                {/* {openMathSelect && isFocused ? <div className='math-func-select-container'>
                    <div className='math-func-select'>
                        <div onClick={() => {
                            setIsFocused(false)
                            setSelectedMathFunction(true)
                        }} tabIndex={0} className='math-func'>Toplama</div>
                        <div tabIndex={0} className='math-func'>Ceil</div>
                        <div tabIndex={0} className='math-func'>Çarpma</div>
                    </div>
                </div> : ""} */}
            </div>
            {
                (requireValidate || !validate.current) && inputMode === 'email' &&
                <div><small style={{ color: '#D75E5E' }}><em> * Enter a valid email address.</em></small></div>
            }
            {
                showObjectExporer && (
                    <ObjectExplorer
                        show={showObjectExporer} cancel={cancelObjectExplorer}
                        variables={variables} setOpenVariable={setOpenVariable}
                        field={{
                            value: state.editorState.getCurrentContent().getPlainText(), name: inputName,
                            type: fieldType, displayName,
                            selectedVariableName: action.fields && action.fields[inputName] ? action.fields[inputName].selectedVariableName : ''
                        }}
                        functions={
                            action.fields && action.fields[inputName]
                                && action.fields[inputName].functions && action.fields[inputName].functions.length
                                ? action.fields[inputName].functions : []
                        }
                        save={saveObjectExplorer}
                        saveNewValue={saveNewValue}
                    // fieldConditions={
                    //     action.fields && action.fields[inputName]
                    //         && action.fields[inputName].conditions && action.fields[inputName].conditions.length
                    //         ? action.fields[inputName].conditions : []
                    // }
                    // selectedVariable={action.fields[inputName]} saveConfiguration={() => { }}
                    //value={assignValues[configureModal.index]} variableRender={variableRender}
                    />
                )
            }
            {
                selectedMathFunction && (
                    <MathFunc setOpen={setSelectedMathFunction} text={state.editorState.getCurrentContent().getPlainText()} addChip={addChip}
                        hideInput={handleClose} editFuncName={selectedMathFunc} selectedFunction={selectedMathFunction} variables={variables} setOpenVariable={setOpenVariable} />
                )
            }
            {
                openDeleteConfirm && (
                    <DeleteConfirm setOpenDelete={setOpenDeleteConfirm}
                        message={t('CONTENT.FUNCTIONS.DELETE-FUNC')}
                        deleteFunc={deleteMathFunc} />
                )
            }

            {
                openDrive && (
                    <MateDrive setOpen={setOpenDrive} flowId={autom_id} from={mateDrivePath ? mateDrivePath : inputName} handleOnChange={selectMateDrive} />
                )
            }
        </>
    );
}

export { TextInput };
